import {isUndefined} from 'lodash';
import {DateTime} from 'luxon';
import React, {FC, ReactNode, useCallback, useEffect, useMemo, useState} from 'react';
import styled from 'styled-components';

import {withTimezone} from '../../../../libs/shared/core/src/helpers/datetime/dateTimeHelpers';
import {CalendarWeekDaysEnum} from '../../../models/api/calendarCommonTypes';
import {StartAndEndDates} from '../../../models/dates/dateIntervalModel';
import {DatePickerGrid} from './datePickerGrid';
import {DatePickerHeader} from './datePickerHeader';
import {DateItemWrapperRendererType} from './dateRangeComboBox';

/*
 * Props.
 */

interface DatePickerProps {
  calendarWeekStartDay: CalendarWeekDaysEnum;
  selectedDate?: DateTime;
  selectedDates?: StartAndEndDates;
  highlightedDate?: DateTime;
  minDate?: DateTime;
  maxDate?: DateTime;
  onDateSelect?: (time: DateTime) => void;
  datePickerGridItemWrapperRenderer?: DateItemWrapperRendererType;
  focusedMonth?: DateTime;
  onFocusedMonthChange?: (arg: DateTime | undefined) => void;
  primaryColor?: string;
  secondaryColor?: string;
  disabledColor?: string;
  backgroundHoverColor?: string;
  hideDifferentMonth?: boolean;
  disabledIcon?: ReactNode;
  timezone?: string;
  hasForwardMonthButton?: boolean;
  hasBackMonthButton?: boolean;
}

/*
 * Style.
 */

const StyledPickerDiv = styled.div`
  display: grid;
  grid-auto-rows: auto;
  grid-row-gap: 8px;
`;

/*
 * Component.
 */

export const DatePicker: FC<DatePickerProps> = (props) => {
  const {
    backgroundHoverColor,
    highlightedDate,
    minDate,
    onDateSelect,
    onFocusedMonthChange,
    datePickerGridItemWrapperRenderer,
    secondaryColor,
    selectedDate: baseSelectedDate,
    disabledIcon,
    timezone,
    hasForwardMonthButton,
    hasBackMonthButton,
  } = props;
  const selectedDate = withTimezone(baseSelectedDate, timezone);
  const selectedDateMonth = selectedDate?.startOf('month');
  const [baseFocusedMonth, setFocusedMonth] = useState(
    () => selectedDateMonth ?? highlightedDate ?? minDate ?? DateTime.local(),
  );
  const focusedMonth = useMemo(() => withTimezone(baseFocusedMonth, timezone), [baseFocusedMonth, timezone]);

  /*
   * When the selected date changes, updated the focused month.
   */

  const selectedDateMonthMillis = selectedDateMonth?.toMillis();
  useEffect(() => {
    if (isUndefined(selectedDateMonthMillis)) {
      return;
    }

    const dt = DateTime.fromMillis(selectedDateMonthMillis);

    if (dt.isValid) {
      setFocusedMonth(dt);
    }
  }, [selectedDateMonthMillis]);

  /*
   * Event handlers.
   */

  const onFocusPreviousMonth = useCallback(() => {
    if (onFocusedMonthChange && props.focusedMonth) {
      onFocusedMonthChange(props.focusedMonth?.minus({months: 1}));
    } else {
      setFocusedMonth((value) => value.minus({months: 1}));
    }
  }, [props.focusedMonth, onFocusedMonthChange]);

  const onFocusNextMonth = useCallback(() => {
    if (onFocusedMonthChange && props.focusedMonth) {
      onFocusedMonthChange(props.focusedMonth?.plus({months: 1}));
    } else {
      setFocusedMonth((value) => value.plus({months: 1}));
    }
  }, [props.focusedMonth, onFocusedMonthChange]);

  return (
    <StyledPickerDiv>
      <DatePickerHeader
        focusedMonth={props.focusedMonth ?? focusedMonth}
        onFocusPreviousMonth={onFocusPreviousMonth}
        onFocusNextMonth={onFocusNextMonth}
        color={secondaryColor}
        backgroundHoverColor={backgroundHoverColor}
        hasForwardMonthButton={hasForwardMonthButton}
        hasBackMonthButton={hasBackMonthButton}
      />
      <DatePickerGrid
        {...props}
        focusedMonth={props.focusedMonth ?? focusedMonth}
        onDateSelect={onDateSelect}
        datePickerGridItemWrapperRenderer={datePickerGridItemWrapperRenderer}
        disabledIcon={disabledIcon}
        primaryColor={props.primaryColor}
        secondaryColor={secondaryColor}
        disabledColor={props.disabledColor}
        backgroundHoverColor={backgroundHoverColor}
        timezone={timezone}
      />
    </StyledPickerDiv>
  );
};
